import React, { useState, useEffect } from 'react';
import { useAppContextProvider } from '../../contextapi';
import { FaArrowDown, FaArrowUp, FaTimes, FaTimesCircle } from 'react-icons/fa';
import { MdFormatClear } from 'react-icons/md';
import { EditorState, Transaction } from '@codemirror/state';

const InlpTab = () => {
  const { inlpActions, setLineFocus, setSidebarClose, setInlpActions, mainView } = useAppContextProvider();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [currentSelection, setCurrentSelection] = useState(null);
  const [autoNavigationActive, setAutoNavigationActive] = useState(false);
  const [currentActionIndex, setCurrentActionIndex] = useState(null);

  // Group INLP actions by title
  const groupedActions = inlpActions.reduce((acc, action) => {
    const normalizedTitle = action.title ? action.title.toLowerCase() : 'unknown';
    
    if (!acc[normalizedTitle]) {
      acc[normalizedTitle] = [];
    }
    acc[normalizedTitle].push(action);
    return acc;
  }, {});

  const isReadOnly = !!mainView?.state?.facet && mainView.state.facet(EditorState.readOnly);

  // Create a flat list of all actions for navigation
  const allActionsList = Object.values(groupedActions).flat();

  // Effect to handle clicking outside to exit auto-navigation mode
  useEffect(() => {
    if (autoNavigationActive) {
      const handleClickOutside = (e) => {
        // Check if the click is outside the INLP tab and tooltip
        const inlpTab = document.querySelector('.inlp-tab');
        const tooltip = document.querySelector('.inlp-tooltip');
        
        if (inlpTab && !inlpTab.contains(e.target) && 
            (!tooltip || !tooltip.contains(e.target))) {
          setAutoNavigationActive(false);
          setCurrentActionIndex(null);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [autoNavigationActive]);

  const toggleGroup = (title) => {
    setExpandedGroups(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const handleActionClick = (action, index) => {
    setCurrentSelection(action);
    setCurrentActionIndex(index);
    setAutoNavigationActive(true);
    setLineFocus({
      from: action.from,
      to: action.to
    });
  };

  // Function to navigate to the next action
  const navigateToNextAction = () => {
    if (currentActionIndex === null || !autoNavigationActive) return;
    
    const nextIndex = currentActionIndex + 1;
    if (nextIndex < allActionsList.length) {
      const nextAction = allActionsList[nextIndex];
      
      // Expand the group if it's not already expanded
      const nextActionTitle = nextAction.title ? nextAction.title.toLowerCase() : 'unknown';
      if (!expandedGroups[nextActionTitle]) {
        setExpandedGroups(prev => ({
          ...prev,
          [nextActionTitle]: true
        }));
      }
      
      // Set the next action as current and focus on it
      setCurrentSelection(nextAction);
      setCurrentActionIndex(nextIndex);
      setLineFocus({
        from: nextAction.from,
        to: nextAction.to
      });
    } else {
      // No more actions, exit auto-navigation mode
      setAutoNavigationActive(false);
      setCurrentActionIndex(null);
      setCurrentSelection(null);
    }
  };

  const handleRejectAction = (action) => {
    console.log("Reject button clicked in sidebar");
    if (!window.appContext || !window.appContext.setInlpActions) {
      console.error("Global app context not available");
      return;
    }
    // Create a unique identifier for this action
    const actionIdentifier = {
      from: action.from,
      to: action.to,
      replaceText: action.replaceText
    };
    
    console.log("Action being rejected:", {
      from: action.from,
      to: action.to,
      highlightText: action.highlightText,
      replaceText: action.replaceText,
      isInsertAction: !action.highlightText || action.highlightText.trim() === ''
    });
  
    // Use the global context to update inlpActions
    window.appContext.setInlpActions(current => {
      // Filter out the rejected action
      const filtered = current.filter(item => {
        return !(
          item.from === actionIdentifier.from && 
          item.to === actionIdentifier.to && 
          item.replaceText === actionIdentifier.replaceText
        );
      });
      console.log("Original inlpActions length:", current.length);
      console.log("Filtered inlpActions length:", filtered.length);
      return filtered;
    });
  
    // Check if this is an insert action (highlightText is empty or just whitespace)
    const isInsertAction = !action.highlightText || action.highlightText.trim() === '';
    window.mainView = mainView;
    // Get the global view
    const globalView = window.mainView || null;
    
    if (globalView) {
      try {
        if (isInsertAction) {
          // For insert actions, check if the text has already been inserted
          const currentText = globalView.state.doc.sliceString(action.from, action.to);
          console.log("Current text at position:", currentText);
          
          if (currentText === action.replaceText) {
            // If the suggested text has already been inserted, we need to remove it
            // But we'll do this WITHOUT creating a track change
            console.log("Insert already applied, removing inserted text without tracking");
            
            // Create a transaction that doesn't trigger track changes
            const tr = globalView.state.update({
              changes: {
                from: action.from,
                to: action.to,
                insert: ''  // Remove the inserted text
              },
              annotations: Transaction.userEvent.of('deletereject')  // Special annotation to bypass track changes
            });
            
            globalView.dispatch(tr);
          } else {
            // If the text hasn't been inserted, just update decorations
            console.log("Insert not applied, only updating decorations");
            // No need to dispatch anything, the decoration will be removed by the state update above
          }
        } else {
          // For replace/delete actions, just update decorations
          console.log("Only updating decorations for replace/delete action");
          // No need to modify the text, just let the decoration be removed
        }
      } catch (error) {
        console.error("Error during rejection handling:", error);
      }
    } else {
      console.error("Global view is not available");
    }
  
    // Navigate to the next action after rejection
    setTimeout(() => {
      navigateToNextAction();
    }, 100);
  };
    
  const displayTitles = {};
  inlpActions.forEach(action => {
    const normalizedTitle = action.title ? action.title.toLowerCase() : 'unknown';
    if (!displayTitles[normalizedTitle]) {
      displayTitles[normalizedTitle] = action.title || 'Unknown';
    }
  });
  
  return (
    <aside className="inlp-tab" aria-label='INLP Suggestions'>
        <div className="slider-header">
        <h2>INLP Suggestions ({inlpActions.length})</h2>
        <button className="close-btn" title="Close">
          <FaTimes onClick={() => {
            setSidebarClose(true);
            setAutoNavigationActive(false);
          }} />
        </button>
      </div>
      <div className="inlp-content">
      {Object.entries(groupedActions).map(([normalizedTitle, actions]) => (
          <section key={normalizedTitle} className="inlp-group">
            <div 
              className="group-header" 
              onClick={() => toggleGroup(normalizedTitle)}
            >
              {/* Use the display title with proper case */}
              <span style={{textTransform:"capitalize"}}>{displayTitles[normalizedTitle]}</span>
              <div>
                <span className="group-count">{actions.length}</span>
                <span className="arrow">
                  {expandedGroups[normalizedTitle] ? <FaArrowUp /> : <FaArrowDown />}
                </span>              
              </div>
            </div>
            {expandedGroups[normalizedTitle] && (
              <div className="group-items">
                {actions.map((action, index) => {
                  // Find the global index of this action in the flat list
                  const globalIndex = allActionsList.findIndex(a => 
                    a.from === action.from && 
                    a.to === action.to && 
                    a.replaceText === action.replaceText
                  );
                  
                  return (
                    <div 
                      key={index} 
                      className={`inlp-item ${currentSelection === action ? 'selected' : ''}`}
                      onClick={() => handleActionClick(action, globalIndex)}
                    >
                      <div className="action-type">{action.action}</div>
                      <div className="inlp-highlighted-text">
                        <span className="strike-text">{action.highlightText ||  
                        <span className="empty-text"><MdFormatClear /> Empty text</span>}</span>
                        {" → "}
                        <span className="replace-text">{action.replaceText}</span>
                      </div>
                   {currentSelection === action && (
                      <div className="inlp-action-buttons">
                        {!isReadOnly && (
                          <button
                            className="reject-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRejectAction(action);
                            }}
                            title="Reject suggestion"
                          >
                            <FaTimesCircle /> Ignore
                          </button>
                        )}
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        ))}
      </div>
    </aside>
  );
};

export default InlpTab;
